
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminStats {
  totalUsers: string;
  activeSessions: string;
  apiCalls: string;
  systemHealth: string;
}

export const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: '0',
    activeSessions: '0',
    apiCalls: '0',
    systemHealth: '99.9%'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching admin stats using RPC...');
      
      // Call the secure admin RPC function
      const { data, error: rpcError } = await supabase
        .rpc('get_admin_statistics');

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        throw new Error(rpcError.message);
      }

      if (!data || data.length === 0) {
        throw new Error('No data returned from stats function');
      }

      const statsData = data[0];
      
      console.log('Stats from RPC:', statsData);
      
      // Format numbers for display
      const formatNumber = (num: number | bigint) => {
        const numValue = Number(num);
        if (numValue >= 1000) {
          return (numValue / 1000).toFixed(1) + 'K';
        }
        return numValue.toString();
      };

      // Use fetched data
      const newStats = {
        totalUsers: formatNumber(statsData.total_users),
        activeSessions: formatNumber(statsData.active_sessions_7d),
        apiCalls: formatNumber(statsData.images_last_7d),
        systemHealth: `${Number(statsData.system_health).toFixed(1)}%`
      };

      console.log('Formatted stats:', newStats);
      setStats(newStats);
      
    } catch (error: any) {
      console.error('Error fetching admin stats:', error);
      setError(error.message || 'Failed to fetch stats');
      
      // Set stats to show error state
      setStats({
        totalUsers: 'N/A',
        activeSessions: 'N/A',
        apiCalls: 'N/A',
        systemHealth: 'N/A'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // Refresh stats every 120 seconds to reduce database load
    const interval = setInterval(fetchStats, 120000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
